import { produce } from 'immer';
import { isIOS, isMobile } from 'react-device-detect';
import { isConfigFlagEnabled } from '../../config';
import { INITIAL_EXPANDABLE_SECTION_STATES } from '../../types/expandableSectionTypes';
import { ALL_PANEL_TYPES } from '../../types/panelTypes';
import { CHANGE_NUM_BOMBS_TO_THROW, DISABLE_BEFORE_UNLOAD, ENSURE_PANEL_HAS_PRIORITY, SET_APP_DIMENSIONS, SET_BAD_BEAT_JACKPOT_AMOUNT_FROM_INBOUND, SET_BOMB_LOADED, SET_CONNECTION_IS_LOST, SET_IS_CLIENT_VERSION_OK, SET_LOADING_IS_DONE, SET_MOBILE_WARNING_MODAL_AS_SHOWN, SET_SESSION_ID_FROM_INBOUND, SET_TEMP_LOGIN_CREDENTIALS, TOGGLE_EXPANDABLE_SECTION, TOGGLE_IS_HI_DPI_ALLOWED } from '../actions/sessionActions';
const ALL_NUM_BOMBS_TO_THROW = [1, 2, 3, 5];
// The number of panel buttons (from the start of the list of panels) that should not move from their positions.
// In other words, cannot be hidden or replaced by panel buttons with a lower priority.
// For example, if the first panel button is 'chat' and this number is 1, the 'chat' panel will always be highest
// priority and shouldn't move from that spot.
const NUM_STATIC_PRIORITY_PANEL_BUTTONS = 1;
export const initialState = {
    wasMobileWarningModalShown: false,
    isConnectionLost: false,
    isLoadingDone: false,
    isBeforeUnloadDisabled: false,
    expandableSectionStates: { ...INITIAL_EXPANDABLE_SECTION_STATES },
    // TODO: Test and enable for Android and other devices:
    isHiDpiAllowed: isConfigFlagEnabled('hiDpi') && isMobile && isIOS,
    numBombsToThrow: 1,
    panelPriority: [...ALL_PANEL_TYPES],
    loadedBombs: {}
};
const sessionReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case SET_TEMP_LOGIN_CREDENTIALS: {
                draft.tempLoginCredentials = action.payload;
                break;
            }
            case SET_MOBILE_WARNING_MODAL_AS_SHOWN: {
                draft.wasMobileWarningModalShown = true;
                break;
            }
            case SET_CONNECTION_IS_LOST: {
                draft.isConnectionLost = true;
                break;
            }
            case SET_LOADING_IS_DONE: {
                draft.isLoadingDone = true;
                break;
            }
            case DISABLE_BEFORE_UNLOAD: {
                draft.isBeforeUnloadDisabled = true;
                break;
            }
            case TOGGLE_EXPANDABLE_SECTION: {
                draft.expandableSectionStates[action.payload] = !draft.expandableSectionStates[action.payload];
                break;
            }
            case TOGGLE_IS_HI_DPI_ALLOWED: {
                draft.isHiDpiAllowed = !draft.isHiDpiAllowed;
                break;
            }
            case CHANGE_NUM_BOMBS_TO_THROW: {
                draft.numBombsToThrow =
                    ALL_NUM_BOMBS_TO_THROW[(ALL_NUM_BOMBS_TO_THROW.indexOf(draft.numBombsToThrow) + 1) % ALL_NUM_BOMBS_TO_THROW.length];
                break;
            }
            case ENSURE_PANEL_HAS_PRIORITY: {
                const { panelType, numEnabledPanels, numVisiblePanelButtons } = action.payload;
                if (numVisiblePanelButtons >= numEnabledPanels) {
                    // all are visible, nothing to do
                    break;
                }
                const panelPriorityIndex = draft.panelPriority.indexOf(panelType);
                if (panelPriorityIndex === -1) {
                    // probably shouldn't happen, ignore
                    break;
                }
                if (panelPriorityIndex < numVisiblePanelButtons) {
                    // already has enough priority to be visible
                    break;
                }
                // swap places with the first non-static-priority panel:
                const [removedPanelType] = draft.panelPriority.splice(panelPriorityIndex, NUM_STATIC_PRIORITY_PANEL_BUTTONS);
                draft.panelPriority.splice(1, 0, removedPanelType);
                break;
            }
            case SET_APP_DIMENSIONS: {
                draft.appDimensions = action.payload;
                break;
            }
            case SET_BAD_BEAT_JACKPOT_AMOUNT_FROM_INBOUND: {
                const { powerPlayerWinnableAmount, nonPowerPlayerWinnableAmount, lastWinner } = action.payload;
                draft.badBeatJackpotProperties = { powerPlayerWinnableAmount, nonPowerPlayerWinnableAmount, lastWinner };
                break;
            }
            case SET_IS_CLIENT_VERSION_OK: {
                draft.isClientVersionOk = action.payload;
                break;
            }
            case SET_BOMB_LOADED: {
                draft.loadedBombs[action.payload] = true;
                break;
            }
            case SET_SESSION_ID_FROM_INBOUND: {
                draft.sessionUuid = action.payload.sessionUuid;
                break;
            }
            default: {
                return state;
            }
        }
    });
};
export default sessionReducer;
